"use client";

import { RiAlertLine, RiRefreshLine, RiWifiOffLine } from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";
import {
	Component,
	type ComponentType,
	type ErrorInfo,
	memo,
	type ReactNode,
} from "react";

interface ErrorBoundaryState {
	hasError: boolean;
	error?: Error;
	errorCount: number;
}

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ComponentType<{ error: Error; retry: () => void }>;
}

export class ErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, errorCount: 0 };
	}

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		// Check if this is a Safari "Load failed" error
		const isSafariLoadError =
			error.message?.includes("Load failed") ||
			error.message?.includes("network") ||
			error.message?.includes("fetch");

		// Log error to console
		console.error("Error caught by boundary:", error, errorInfo);

		// Auto-retry for Safari load errors (up to 3 times)
		if (isSafariLoadError && this.state.errorCount < 3) {
			this.setState((prevState) => ({ errorCount: prevState.errorCount + 1 }));

			setTimeout(
				() => {
					this.setState({ hasError: false, error: undefined });
				},
				1000 * (this.state.errorCount + 1),
			); // Exponential backoff
		}
	}

	render() {
		if (this.state.hasError && this.state.error) {
			const FallbackComponent = this.props.fallback || DefaultErrorFallback;

			return (
				<FallbackComponent
					error={this.state.error}
					retry={() =>
						this.setState({ hasError: false, error: undefined, errorCount: 0 })
					}
				/>
			);
		}

		return this.props.children;
	}
}

const DefaultErrorFallback = memo(
	({ error, retry }: { error: Error; retry: () => void }) => {
		// Check if it's a Safari-specific error
		const isSafariError =
			typeof navigator !== "undefined" &&
			/safari/i.test(navigator.userAgent) &&
			!/chrome|chromium|crios/i.test(navigator.userAgent);

		const errorMessage = error.message || "An unexpected error occurred";
		const isNetworkError =
			errorMessage.includes("Load failed") ||
			errorMessage.includes("network") ||
			errorMessage.includes("fetch");

		return (
			<div className="flex min-h-screen items-center justify-center p-4">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
							{isNetworkError ? (
								<RiWifiOffLine className="h-6 w-6 text-destructive" />
							) : (
								<RiAlertLine className="h-6 w-6 text-destructive" />
							)}
						</div>
						<CardTitle>
							{isNetworkError ? "Connection Issue" : "Something went wrong"}
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<p className="text-center text-muted-foreground text-sm">
							{isSafariError && isNetworkError
								? "Safari is having trouble loading this page. This sometimes happens with Safari."
								: isNetworkError
									? "We're having trouble connecting. Please check your internet connection."
									: "We've been notified about this issue and are working to fix it."}
						</p>

						{process.env.NODE_ENV === "development" && (
							<div className="rounded-md bg-muted p-3">
								<p className="font-mono text-muted-foreground text-xs">
									{error.message}
								</p>
							</div>
						)}

						<Button onClick={retry} className="w-full" variant="outline">
							<RiRefreshLine className="mr-2 h-4 w-4" />
							Try again
						</Button>

						{isSafariError && isNetworkError && (
							<p className="text-center text-muted-foreground text-xs">
								Tip: If this keeps happening, try refreshing the page or using a
								different browser.
							</p>
						)}
					</CardContent>
				</Card>
			</div>
		);
	},
);

DefaultErrorFallback.displayName = "DefaultErrorFallback";

export default ErrorBoundary;
